// src/services/vibes.js — the Vibes social layer, speaking the app's exact schema.
//
// Everything here was mapped from the app before it was written:
//   posts        top-level `posts` — likes: uid[], comments: object[] ON the doc,
//                media: plaintext Firebase Storage URLs (vibes is public-by-design,
//                unlike chats, which are E2EE on R2 — do not "fix" this asymmetry)
//   saved        users/{uid}.savedPosts: postId[] + posts.savesCount counter
//   friends      accepted docs in top-level `friendRequests` (no friends array exists)
//   notifs       top-level `notifications`, where('toUserId','==',me)
//   gate         users/{uid}.dateOfBirth (Timestamp) + gender; LEGAL_AGE = 16

import {
  collection, doc, query, where, orderBy, limit, onSnapshot, getDocs, getDoc,
  addDoc, setDoc, updateDoc, arrayUnion, arrayRemove, increment, serverTimestamp, writeBatch,
  runTransaction, Timestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { getSocket } from './socket';
import { postFromDoc, likeCountOf } from './postCounts';
import { triggerTranscode, triggerOptimizeImage } from './rightsApi';
import { soundFieldsFor, soundCredited, MIX_DEFAULT } from './soundLibrary';
/* 🖼 the one image normaliser — an iPhone's HEIC is unreadable in every browser
   but Safari, and a post reached none of the chat path's conversion */
import { normalizeImageFile } from './mediaTools';
import { buildSharePost, shareAudience } from './sharedPost';
import { buildComment, withoutComment, recipientsForComment, COMMENT_MAX_LEN, countAfterArrayChange, commentCountOf } from './vibeComments';
/* 💬 the server writes the comment (safety check, notifications) — same route the phone uses */
import { postVibeComment, deleteVibeComment } from './vibeCommentApi';
/* ❤️ likes and shares a viral post can hold (server/functions/likeRoutes.js) */
import { myLikes, sendLike, countShare, reactionOf, rememberReaction } from './likeApi';
import { pressReaction, dropReaction } from './reactionLive';   // 😍 my press, on every copy at once
import { isPagedLikes, shareLedgerRoom } from './likeLedger';
/* 🏷 / ⚠️ shared with the phone and the admin server — see services/videoTags */
import { tagsForPost, safetyForPost } from './videoTags';
import { cleanFeeling, musicFields } from './postCompose';   // ✍️ feeling / activity, and a song for a post with no video
/* 🔒 the one function the phone, the browser and the security rule all agree on */
import { whyCannotSend } from './friendRequests';
import { hiddenPath } from './profileStore';
/* 👥 a page post is not a friend's post — see services/friendFeed */
import { scoresAsFriend } from './friendFeed';
/* 🎞 a still for a video post — the browser never made one; see videoPoster */
import { posterFromVideo } from './videoPoster';
/* 🎨 the editor's catalogue, shared with the phone — see services/mediaEditor */
import { filterColorOf } from './mediaEditor';
/* 🖼 every picture's stickers, each tagged with its picture */
import { collectPostStickers, rememberMyAnswer } from './vibeStickers';
/* 😍📊 sticker answers go through the server (see answerSticker) */
import { auth } from '../config/firebase';
import { storyLook, STORY_TTL_MS, STORY_BATCH_MAX, VIDEO_MAX_MS } from './stories';
/* 🎂 my date of birth lives in users/{uid}/private/dob · 🏷 tags are decided by the server */
import { readMyDob, knownDob, rememberDob, announceTags } from './vibeSocial';
import { sendDob, dobOf } from './legal';   // ⚖️ the server sets the date of birth, once
import { BACKEND_URL } from './socket';

export const LEGAL_AGE = 16;

/* ── 🔔 phone push — the app's exact 'send-notification' socket event ─────────
   The signaling server catches it, looks up the recipient's device tokens and
   pushes to EVERY phone via FCM/APNs (pushToAllDevices), plus a live
   'social-notification' to any connected sockets. locTitleKey/locKey/locParams
   let each receiving device render the text in ITS OWN app language; the
   literal title/body are the fallback. Fire-and-forget — a dead socket must
   never break the like/comment itself. */
function pushSocial({ toUserId, type, title, body, locTitleKey, locKey, locParams, image, payload }) {
  try {
    const s = getSocket();
    if (s && s.connected) {
      s.emit('send-notification', {
        toUserId, type, title, body,
        locTitleKey, locKey, locParams,
        image: image || '',
        payload: payload || {},
      });
    }
  } catch {}
}

const nameOf = (me) => me.fullName || me.username || 'Someone';

export function ageOf(dob) {
  if (!dob) return null;
  const d = dob?.toDate ? dob.toDate() : new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let a = now.getFullYear() - d.getFullYear();
  if (now.getMonth() < d.getMonth() || (now.getMonth() === d.getMonth() && now.getDate() < d.getDate())) a -= 1;
  return a;
}

/** The registration gate + my vibe account state, live off my own user doc. */
/* 🎂 the public document, with my date of birth from the private one when the
   public field is gone — read once, then answered from memory */
function withDob(uid, d, emit) {
  if (d.dateOfBirth) { emit(d); return; }
  const k = knownDob(uid);
  if (k !== undefined) { emit(k ? { ...d, dateOfBirth: k } : d); return; }
  readMyDob(uid).then((x) => emit(x ? { ...d, dateOfBirth: x } : d)).catch(() => emit(d));
}

export function watchVibeSelf(uid, cb) {
  return onSnapshot(doc(db, 'users', uid), (snap) => withDob(uid, snap.data() || {}, (d) => {
    const a = ageOf(d.dateOfBirth);
    cb({
      registered: !!d.dateOfBirth,
      underage: a != null && a < LEGAL_AGE,
      age: a,
      dob: d.dateOfBirth || null,     // raw, so the underage screen can date the unlock
      gender: d.gender || '',
      strikes: Number(d.strikes) || 0,
      banExpiresAt: d.banExpiresAt || null,
      savedPosts: Array.isArray(d.savedPosts) ? d.savedPosts : [],
      hiddenPosts: new Set(Array.isArray(d.hiddenPosts) ? d.hiddenPosts : []),
      lessLike: new Set(Array.isArray(d.lessLike) ? d.lessLike : []),
      blocked: new Set(Array.isArray(d.blockedUsers) ? d.blockedUsers : []),
      fullName: d.fullName || d.username || '',
      username: d.username || '',
      profileImage: d.profileImage || '',
    });
  }), () => cb(null));
}

/** The Vibes gate's answer — given to the SERVER, which sets it once (server/functions/legal.js):
 *  the phone's gate does the same (src/home/ageGate/AgeGateHost). Throws with the server's reason. */
export async function completeVibeRegistration(uid, dob, gender) {
  /* 🟥 the date field gives 'YYYY-MM-DD' — and `new Date('YYYY-MM-DD')` is midnight UTC, which west of
        Greenwich is the PREVIOUS day: everyone in the Americas was a day older than they said. The
        string is the day picked; it goes to the server as it is. */
  const day = typeof dob === 'string' ? dob : dobOf(dob);
  const r = await sendDob(day, gender);
  if (!r.ok) { const e = new Error(r.error); e.code = r.error; throw e; }
  const [y, m, d] = day.split('-').map(Number);
  rememberDob(uid, new Date(y, m - 1, d));   // this tab's copy, so the gate answers without a re-read
}

/** The date Vibes opens for someone born on `dob` — the app's own arithmetic
 *  (HomeScreen: unlockDate = dob, setFullYear(dob + LEGAL_AGE)). */
export function vibesUnlockDate(dob) {
  if (!dob) return null;
  const d = dob?.toDate ? dob.toDate() : new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const u = new Date(d.getTime());
  u.setFullYear(d.getFullYear() + LEGAL_AGE);
  return u;
}

/**
 * 🟥 THE VIEWER'S GATE, AS A STANDALONE LISTENER.
 *
 * MessageBubble's shared_post branch gates on `userProfile?.dateOfBirth` — MY
 * profile, not the sender's. Someone who never finished the Vibes questions, or
 * who is under 16, sees a locked card instead of the post. That decision has to
 * be available inside the CHAT, not just on the Vibes tab, so this is a separate
 * tiny listener rather than a prop threaded down from a screen that may not be
 * mounted.
 *
 * `ready` matters: `!dateOfBirth` is true before the doc arrives, which would
 * flash "Post locked" on every shared post for a fraction of a second on every
 * chat open. Nothing renders a verdict until the snapshot lands.
 */
export function watchVibeGate(uid, cb) {
  if (!uid) { cb({ ready: false, registered: false, underage: false, unlockAt: null }); return () => {}; }
  return onSnapshot(doc(db, 'users', uid), (snap) => withDob(uid, snap.data() || {}, (d) => {
    const a = ageOf(d.dateOfBirth);
    cb({
      ready: true,
      registered: !!d.dateOfBirth,
      underage: a != null && a < LEGAL_AGE,
      age: a,
      unlockAt: vibesUnlockDate(d.dateOfBirth),
    });
  }), () => cb({ ready: false, registered: false, underage: false, unlockAt: null }));
}

/* ── the feed ─────────────────────────────────────────────────────────────── */

/** The app's smart feed, faithfully: last 50 by time, blocked filtered client-side,
 *  then scored — freshness + engagement + a friend bonus — exactly its formula. */
/**
 * @param getHidden   () => Set of postIds I hid       (users/{me}.hiddenPosts)
 * @param getLessLike () => Set of authorUids I muted  (users/{me}.lessLike)
 */
export function watchFeed(uid, getBlocked, getFriends, cb, getHidden, getLessLike) {
  const q = query(collection(db, 'posts'), orderBy('timestamp', 'desc'), limit(50));
  return onSnapshot(q, (snap) => {
    const blocked = getBlocked?.() || new Set();
    const friends = getFriends?.() || new Set();
    const hidden = getHidden?.() || new Set();
    const lessLike = getLessLike?.() || new Set();
    const now = Date.now();
    const posts = [];
    snap.forEach((d) => {
      /* 🟢 the same mapper the phone uses — services/postCounts.ts. A like is an
         ARRAY on the document and there is no `likesCount` field to spread. */
      const p = postFromDoc(d.id, d.data());
      if (blocked.has(p.userId)) return;
      // 🟢 the read that was missing: the app has always WRITTEN hiddenPosts and
      //    never consulted it, so a hidden post came back on the next launch
      if (hidden.has(p.id)) return;
      const ts = p.timestamp?.toDate ? p.timestamp.toDate().getTime() : now;
      const hours = Math.max(0, (now - ts) / 36e5);
      let score = 500 / Math.pow(hours + 1, 1.2);
      score += ((p.shares?.length || 0) * 10) + (commentCountOf(p) * 5) + (likeCountOf(p) * 2);
      /* 👥 🟥 A PAGE POST IS NOT A FRIEND'S POST. `userId` on a page post is the
         account that published it, so a friend who runs a page was lifting that
         page's posts up the feed as though they were his own. The phone had the
         same mistake as a filter on its friends tab; both now ask the one
         predicate in services/friendFeed. */
      const friendly = scoresAsFriend(p, friends);
      if (friendly) score += 200;
      if (p.userId === uid && hours < 1) score += 10000;      // my fresh post pins on top
      else if (friendly && hours < 1) score += 5000;
      /* 🟢 "show less like this" is a WEIGHT, not a filter. Demoted hard enough
         to fall to the bottom of the page, but still reachable — a soft
         preference that silently became a permanent invisible mute would be a
         worse product and an impossible one to debug. */
      if (lessLike.has(p.userId)) score -= 900;
      posts.push({ ...p, _ts: ts, _score: score });
    });
    posts.sort((a, b) => b._score - a._score);
    cb(posts);
  }, () => cb([]));
}

/* ❤️ THE SERVER WRITES THE LIKE — its own document, a buffered counter, and
   the name in the post only while there is room. The heart changes at once
   (the shared store), and goes back if the server refuses. Not reached after
   retries → the old direct write, but ONLY on a post that is not paged yet. */
export async function toggleLike(postId, uid, liked, post = null, reaction = null) {
  myLikes.setMe(uid);
  /* 😍 already liked + a kind picked: only the kind changes, never the count */
  const prevRx = reactionOf(post || { id: postId }, uid, liked);
  if (reaction && liked) {
    if (prevRx === reaction) return;
    rememberReaction(postId, reaction);
    /* 😍 every copy of the post shows it now (services/reactionLive) */
    if (post) pressReaction(post, uid, prevRx, reaction);
    const r = await sendLike(postId, true, 3, reaction);
    if (!r.ok) { rememberReaction(postId, prevRx); dropReaction(postId); const e = new Error('react_refused'); e.code = r.error || 'unreachable'; throw e; }
    return;
  }
  rememberReaction(postId, liked ? null : (reaction || 'like'));
  if (post) pressReaction(post, uid, prevRx, liked ? null : (reaction || 'like'));
  myLikes.press(postId, !liked);
  const served = await sendLike(postId, !liked, 3, liked ? 'like' : (reaction || 'like'));
  if (served.ok) { myLikes.settle(postId, !liked); return; }
  if (served.status || isPagedLikes(post)) {
    myLikes.settle(postId, liked);
    dropReaction(postId);
    const e = new Error('like_refused'); e.code = served.error || 'unreachable'; throw e;
  }
  try {
    await updateDoc(doc(db, 'posts', postId), {
      likes: liked ? arrayRemove(uid) : arrayUnion(uid),
    });
    myLikes.settle(postId, !liked);
  } catch (e) {
    myLikes.settle(postId, liked);
    dropReaction(postId);
    throw e;
  }
}

/* 📊 ANSWERING A STICKER — a poll vote or a drag of the loving indicator.
   🟥 THE WHOLE ARRAY IS REWRITTEN, IN A TRANSACTION. `stickers` is an array of
      objects, and Firestore cannot update one element of one of those in place:
      arrayUnion/arrayRemove match whole objects, and a dotted path needs an
      index that is not stable across an edit. The phone reads-then-writes for
      the same reason. The transaction is the part the phone does NOT do, and is
      why this is here rather than copied from it — two people answering the
      same poll within a few hundred milliseconds is the ordinary case, not the
      race you never see, and a plain read-modify-write drops one of the votes.
   🟢 THE SHAPE MATCHES THE PHONE EXACTLY: `votes[uid] = 0|1` for a poll,
      `values[uid] = 0..1` for a slider, so either client can read the other's. */
export async function answerSticker(postId, stickerId, kind, value, uid) {
  if (!postId || !stickerId || !uid) return false;
  /* 😍📊 🟥 THE DIRECT WRITE WAS REFUSED for everyone but the author — the rules
     do not let a stranger write `stickers` (it is everybody's answers in one
     array), so a vote or a slide never stuck. The server writes it, in a
     transaction, with the same rule both clients mirror (applyStickerAnswer).
     🟢 Remembered on this tab first, so every copy of the post shows it. */
  rememberMyAnswer(postId, stickerId, value);
  for (let attempt = 0; attempt < 3; attempt += 1) {
    let token = null;
    try { token = await auth.currentUser?.getIdToken(); } catch { token = null; }
    if (!token) return false;
    try {
      const res = await fetch(`${BACKEND_URL}/api/vibes/sticker/answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ postId, stickerId, value }),
      });
      let j = null;
      try { j = await res.json(); } catch { j = null; }
      if (res.ok && j && j.ok) return true;
      if (res.status >= 400 && res.status < 500) return false;
    } catch { /* not reached — retry */ }
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
  }
  return false;
}

export async function toggleSave(uid, postId, saved) {
  await updateDoc(doc(db, 'users', uid), {
    savedPosts: saved ? arrayRemove(postId) : arrayUnion(postId),
  });
  await updateDoc(doc(db, 'posts', postId), { savesCount: increment(saved ? -1 : 1) }).catch(() => {});
}

/**
 * Add a comment, or a reply to one.
 *
 * 🟢 THE OBJECT IS BUILT BY services/vibeComments.js, which the phone also uses
 *    — including the rule that a reply to a reply is stored against the
 *    TOP-LEVEL comment rather than against the reply. Two clients writing
 *    slightly different comment objects is a thread that reads one way here and
 *    another way there.
 */
/**
 * Write a comment. `textOrComment` is the words, or a comment the composer
 * already built (VibeComments does, so the row can appear as "Posting…" under
 * an id the server will KEEP — the same id on the local row and on the
 * document's copy is what makes them one comment, and what makes a retry
 * idempotent).
 */
export async function addComment(post, me, textOrComment, replyingTo = null) {
  const comment = textOrComment && typeof textOrComment === 'object' ? textOrComment : buildComment({
    me: {
      uid: me.uid,
      name: me.fullName || me.username || 'Allo user',
      image: me.profileImage || '',
    },
    text: textOrComment,
    replyingTo,
  });
  if (!comment) {
    const e = new Error(String(textOrComment || '').trim().length > COMMENT_MAX_LEN ? 'too_long' : 'empty');
    e.code = e.message; throw e;
  }

  /* 💬 THE SERVER WRITES IT — the safety check runs first, the notifications
     (bell + push, with the comment id) are written there, and a page's post
     notifies the page's OWNER. Not reachable at all → the direct write below,
     exactly as before, and the server's sweep judges it within ten minutes.
     A 4xx is a real answer and is thrown to the composer. */
  const served = await postVibeComment({
    postId: post.id, text: comment.content, replyToId: replyingTo?.id || null, id: comment.id,
    mentions: comment.mentions || [], mentionNames: comment.mentionNames || {},
  });
  if (served.ok) return served.comment;
  if (served.status) { const e = new Error(served.error || 'rejected'); e.code = served.error; throw e; }
  const text = comment.content;
  /* 📄 a paged post's counter is the total — the array alone no longer is */
  await updateDoc(doc(db, 'posts', post.id), post && post.commentsV2 === true
    ? { comments: arrayUnion(comment), commentsCount: increment(1) }
    : { comments: arrayUnion(comment) });

  /* 🔔 WHO HEARS ABOUT THIS (the fallback path only — the server does this when it can). The post's owner used to be the only answer, so
     answering somebody notified a person who might not be in the conversation
     and said nothing at all to the one being answered. The rule is shared with
     the phone — see services/vibeComments — so a reply written on either client
     produces the same rows for the same people. */
  for (const r of recipientsForComment({
    postOwnerUid: post.userId, authorUid: me.uid, replyingTo,
  })) {
    notify({
      type: r.kind, fromMe: me, toUserId: r.uid, post,
      commentContent: text.slice(0, 100),
      parentCommentContent: r.kind === 'reply' ? String(replyingTo?.content || '').slice(0, 100) : '',
    }).catch(() => {});
    pushSocial({
      toUserId: r.uid, type: r.kind,
      title: r.kind === 'reply' ? 'New Reply' : 'New Comment',
      body: r.kind === 'reply'
        ? `${nameOf(me)} replied to your comment`
        : `${nameOf(me)} commented: "${text}"`,
      locTitleKey: r.kind === 'reply' ? 'reply_title' : 'comment_title',
      locKey: r.kind === 'reply' ? 'reply_body' : 'comment_body',
      locParams: { name: nameOf(me), text },
      image: me.profileImage, payload: { postId: post.id },
    });
  }
  return comment;
}

export async function likeWithNotify(post, me, liked, reaction = null) {
  await toggleLike(post.id, me.uid, liked, post, reaction);
  if (!liked && post.userId && post.userId !== me.uid) {
    notify({ type: 'like', fromMe: me, toUserId: post.userId, post }).catch(() => {});
    pushSocial({
      toUserId: post.userId, type: 'like',
      title: 'New Like', body: `${nameOf(me)} liked your vibe.`,
      locTitleKey: 'like_title', locKey: 'like_body', locParams: { name: nameOf(me) },
      image: me.profileImage, payload: { postId: post.id },
    });
  }
}

/**
 * Remove a comment and everything that answered it.
 *
 * 🟥 A REWRITE IN A TRANSACTION, NEVER arrayRemove. arrayRemove matches on deep
 *    equality of the whole element, and a comment's `timestamp` does not survive
 *    the round trip unchanged — the phone shipped that bug and the delete
 *    silently did nothing while the list said it had worked. Rewriting by id is
 *    the only match that holds; the transaction is because two people commenting
 *    while this runs would otherwise be erased by a stale copy of the array.
 *
 * 🟥 `commentsCount` IS WRITTEN TOO. The admin route writes it, and
 *    `commentsOf()` in vibeStats prefers a stored count whenever it is above
 *    zero — so a delete that left it alone would freeze the badge at the old
 *    number for good.
 *
 * 🟥 THE PERMISSION IS THE CALLER'S TO CHECK, and the rules cannot help: they
 *    let any signed-in account write `comments` wholesale. `canDeleteComment` is
 *    what the UI asks; this is what performs it.
 */
/* 📄 THE SERVER REMOVES IT NOW — wherever it lives (its own document, or the
   old array), with every reply, and moves the counter; it checks
   canDeleteComment the way the button does. Resolves to the removed ids.
   Not reachable → the old transaction, which can only reach the old array. */
export async function deleteComment(postId, commentId) {
  const served = await deleteVibeComment({ postId, commentId: String(commentId) });
  if (served.ok) return served.removed;
  if (served.status === 404) return [String(commentId)];
  if (served.status) { const e = new Error(served.error || 'refused'); e.code = served.error; throw e; }
  const ref = doc(db, 'posts', postId);
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    if (!snap.exists()) throw new Error('not_found');
    const data = snap.data() || {};
    const live = data.comments || [];
    if (!live.some((c) => c && String(c.id) === String(commentId))) throw new Error('unreachable');
    const next = withoutComment(live, String(commentId));
    const kept = new Set(next.map((c) => String(c && c.id)));
    /* 🟥 a paged post's counter is the total, not this array's length */
    const cnt = countAfterArrayChange(data, live.length, next.length);
    tx.update(ref, { comments: next, commentsCount: 'set' in cnt ? cnt.set : increment(cnt.delta) });
    return live.map((c) => String((c && c.id) || '')).filter((id) => id && !kept.has(id));
  });
}

function notify({ type, fromMe, toUserId, post, commentContent, parentCommentContent }) {
  return addDoc(collection(db, 'notifications'), {
    type,
    fromUserId: fromMe.uid,
    fromUserName: fromMe.fullName || fromMe.username || 'Allo user',
    fromUserImage: fromMe.profileImage || '',
    toUserId,
    postId: post?.id || null,
    postContent: (post?.content || '').slice(0, 100),
    postImageUrl: post?.imageUrl || '',
    ...(commentContent ? { commentContent } : {}),
    /* 🟢 what THEY said, kept beside what was said back — a reply row showing
       only the answer reads as a stray line of somebody else's text */
    ...(parentCommentContent ? { parentCommentContent } : {}),
    timestamp: serverTimestamp(),
    read: false,
  });
}

/** Text + images/videos → Firebase Storage (the app's paths), then one posts doc. */
/* ── what the media editor produced ───────────────────────────────────────── */

/**
 * 🟥 A DRAFT STICKER CARRIES A `File`, AND A `File` CANNOT GO IN A DOCUMENT.
 *    Firestore rejects it outright, and even if it did not, an editor's private
 *    handle is not part of a post. Every draft-only field is stripped here, in
 *    one place, rather than remembered at each of the call sites that build one.
 */
function stripDraft(st) {
  if (!st) return st;
  const { _file, ...rest } = st;
  return rest;
}

/**
 * One edit record → the post fields both clients read.
 *
 * 🟥 THE NAMES ARE THE PHONE'S NAMES, to the letter — `overlayTextStyle`,
 *    `overlayTextX`, `filterColor`. Tidier ones would produce posts the phone
 *    renders as blank pictures, which is the failure the whole overlay port
 *    exists to end. ui/MediaEditor's `editToFields` is the same mapping on the
 *    other side of the wire; this is the copy the writer trusts.
 */
function editOverlayFields(e) {
  if (!e) return null;
  return {
    filterName: e.filter || 'pure',
    filterColor: filterColorOf(e.filter),
    filterIntensity: Number.isFinite(e.intensity) ? e.intensity : 100,
    overlayText: e.text || '',
    overlayTextColor: e.color || '#FFFFFF',
    overlayTextStyle: e.mode || 'shadow',
    overlayTextBg: e.mode === 'box',
    overlayTextAlign: e.align || 'center',
    overlayTextSize: e.size,
    overlayTextFont: 'System',
    overlayTextX: e.pos?.x || 0,
    overlayTextY: e.pos?.y || 0,
    stickers: Array.isArray(e.stickers) ? e.stickers : [],
    ...(e.trim ? { trimStartMs: Math.round(e.trim.start), trimEndMs: Math.round(e.trim.end) } : {}),
    /* 🎵 the song under the clip — credit, licence, start and mix, from the one
       writer both clients use (services/soundLibrary) */
    ...(e.sound && e.sound.track
      ? { ...soundFieldsFor(e.sound.track, e.sound.mix || MIX_DEFAULT, e.sound.startMs || 0), ...(soundCredited(e.sound.track, e.sound.mix || MIX_DEFAULT) ? { audioTrackId: String(e.sound.track.id) } : {}) }
      : {}),
  };
}

/* ═══ 📸 STORIES ═══════════════════════════════════════════════════════════
   Each picked file becomes a story, with the look the editor gave it — the
   same fields a post carries (editOverlayFields), kept by services/stories.
   storyLook. The file goes up first under the post folders; the document is
   written only for a file that arrived (the rules allow only my own, ≤ 25 h). */
export async function publishStories({ me, files = [], edits = [], audience = 'friends', onProgress = null }) {
  const list = files.slice(0, STORY_BATCH_MAX);
  let posted = 0; let failed = 0;
  for (let i = 0; i < list.length; i += 1) {
    if (onProgress) onProgress(i, list.length);
    try {
      // eslint-disable-next-line no-await-in-loop
      const f = await normalizeImageFile(list[i]);
      const isVideo = /^video\//.test(f.type);
      const stamp = `${Date.now()}_${i}`;
      const path = `${isVideo ? 'postVideos' : 'postImages'}/${me.uid}/story_${stamp}.${isVideo ? 'mp4' : 'jpg'}`;
      const r = ref(storage, path);
      // eslint-disable-next-line no-await-in-loop
      await uploadBytes(r, f, { contentType: f.type || (isVideo ? 'video/mp4' : 'image/jpeg') });
      // eslint-disable-next-line no-await-in-loop
      const url = await getDownloadURL(r);
      let thumbUrl = null; let thumbPath = '';
      let durationMs = 0;
      if (isVideo) {
        // eslint-disable-next-line no-await-in-loop
        const poster = await posterFromVideo(f).catch(() => null);
        if (poster) {
          try {
            thumbPath = `postThumbnails/${me.uid}/story_${stamp}.jpg`;
            const pRef = ref(storage, thumbPath);
            // eslint-disable-next-line no-await-in-loop
            await uploadBytes(pRef, poster, { contentType: 'image/jpeg' });
            // eslint-disable-next-line no-await-in-loop
            thumbUrl = await getDownloadURL(pRef);
          } catch { thumbUrl = null; thumbPath = ''; }
        }
        // eslint-disable-next-line no-await-in-loop
        durationMs = await new Promise((res) => {
          const v = document.createElement('video');
          v.preload = 'metadata';
          v.onloadedmetadata = () => { const d = v.duration; URL.revokeObjectURL(v.src); res(Number.isFinite(d) ? Math.round(d * 1000) : 0); };
          v.onerror = () => { URL.revokeObjectURL(v.src); res(0); };
          v.src = URL.createObjectURL(f);
        });
      }
      const fields = editOverlayFields(edits[i]) || {};
      const stickers = [];
      for (const st of fields.stickers || []) {
        if (st?.type !== 'gallery_image' || !st?._file) { stickers.push(stripDraft(st)); continue; }
        try {
          const sr = ref(storage, `postImages/${me.uid}/story_sticker_${Date.now()}_${st.id}.jpg`);
          // eslint-disable-next-line no-await-in-loop
          await uploadBytes(sr, st._file, { contentType: st._file.type || 'image/jpeg' });
          // eslint-disable-next-line no-await-in-loop
          stickers.push({ ...stripDraft(st), imageUrl: await getDownloadURL(sr) });
        } catch { /* a sticker only this tab can see is dropped */ }
      }
      const look = storyLook({ ...fields, stickers, type: isVideo ? 'video' : 'image' });
      // eslint-disable-next-line no-await-in-loop
      await addDoc(collection(db, 'stories'), {
        userId: me.uid, userName: me.fullName || me.username || 'Allo user', userImage: me.profileImage || '',
        type: isVideo ? 'video' : 'image',
        url, storagePath: path,
        ...(thumbUrl ? { thumbUrl, thumbPath } : {}),
        ...(isVideo && durationMs > 0 ? { durationMs: Math.min(VIDEO_MAX_MS, durationMs) } : {}),
        audience: audience === 'everyone' ? 'everyone' : 'friends',
        ...look,
        createdAt: serverTimestamp(),
        expiresAt: Timestamp.fromMillis(Date.now() + STORY_TTL_MS),
      });
      posted += 1;
    } catch (e) {
      failed += 1;
    }
  }
  if (onProgress) onProgress(list.length, list.length);
  return { posted, failed };
}

export async function createPost({ me, text, files = [], audience = 'everyone', asPage = null, status = null, commentsEnabled = true, textStyle = null, poll = null, mediaEdits = null, aiDeclared = false, tagged = [], feeling = null, music = null }) {
  const media = [];
  /* 🟥 THE POSTER FOR A VIDEO POST, WHICH THIS PATH NEVER MADE. The phone grabs
     a frame, uploads it, and writes it to `imageUrl` — the field every surface
     reads as a video's still: the feed, the reels player and the moderation
     console. The browser wrote `imageUrl: ''` and moved on, so a clip posted
     here had no still anywhere at all. Fail-soft: null just means the post is
     exactly as it would have been. See services/videoPoster. */
  let posterUrl = '';
  for (const raw of files) {
    /* 🖼 🟥 A HEIC WENT UP UNDER A `.jpg` PATH WITH `contentType: image/heic`,
       which is the worst of both: the name promises a JPEG and the header
       promises something no browser can decode, so every viewer draws a broken
       image. This path never went near `mediaTools` either. */
    // eslint-disable-next-line no-await-in-loop
    const f = await normalizeImageFile(raw);
    const isVideo = /^video\//.test(f.type);
    const path = `${isVideo ? 'postVideos' : 'postImages'}/${me.uid}/${Date.now()}_${media.length}.${isVideo ? 'mp4' : 'jpg'}`;
    const r = ref(storage, path);
    // eslint-disable-next-line no-await-in-loop
    await uploadBytes(r, f, { contentType: f.type });
    // eslint-disable-next-line no-await-in-loop
    const url = await getDownloadURL(r);
    media.push({ id: `${Date.now()}_${media.length}`, url, type: isVideo ? 'video' : 'image', storagePath: path });

    /* 🟢 ONLY THE FIRST ITEM, because `imageUrl` is the post's single poster and
       the phone stamps it from the first item too — a second video's frame would
       overwrite the still for a clip nobody is looking at. */
    if (isVideo && media.length === 1) {
      // eslint-disable-next-line no-await-in-loop
      const poster = await posterFromVideo(f);
      if (poster) {
        try {
          const pPath = `postThumbnails/${me.uid}/${Date.now()}.jpg`;
          const pRef = ref(storage, pPath);
          // eslint-disable-next-line no-await-in-loop
          await uploadBytes(pRef, poster, { contentType: 'image/jpeg' });
          // eslint-disable-next-line no-await-in-loop
          posterUrl = await getDownloadURL(pRef);
        } catch {
          /* 🟥 A POSTER IS NOT WORTH A POST. If the upload fails the clip still
             goes up, exactly as it did before this existed. */
        }
      }
    }
  }
  const first = media[0];

  /* 🎨 WHAT THE EDITOR DID — filter, drawn-on caption, stickers, trim.
     🟥 STAMPED FROM THE FIRST ITEM, because that is what the phone stamps from
        and what every renderer on both clients reads. A browser that stamped
        the post from some other item would make posts the phone draws with the
        wrong sticker on the wrong photo.
     🟢 WRITTEN ONLY WHEN THERE IS SOMETHING TO WRITE: an ordinary post's
        document stays exactly the shape it has always been. */
  let overlay = mediaEdits && mediaEdits[0] ? editOverlayFields(mediaEdits[0]) : null;
  /* 🖼 🟥 THE FIRST PICTURE'S STICKERS WERE THE ONLY ONES WRITTEN — a sticker
     placed on photo 2 vanished at publish, on this client and the phone alike.
     Every picture's stickers now go into the one `stickers` array, tagged with
     the picture they sit on (picture 0 untagged — the shape older posts have).
     The caption and filter stay the first picture's: that is what both
     clients draw, and only on that picture. */
  const everySticker = Array.isArray(mediaEdits)
    ? collectPostStickers(mediaEdits.map((e) => (e && Array.isArray(e.stickers) ? e.stickers : [])))
    : [];
  if (everySticker.length) {
    if (!overlay) overlay = { stickers: [] };
    overlay.stickers = everySticker;
  }
  if (overlay) {
    /* 🖼 🟥 AN IMAGE STICKER HAS TO BE UPLOADED, and this is the same bug the
       phone had: the picker hands back a local handle, and a `blob:` URL in a
       post document is a picture only the tab that made it can see. Fail-soft
       per sticker — one that will not upload is dropped rather than taking the
       whole post down or shipping a URL nobody else can load. */
    const out = [];
    for (const st of overlay.stickers || []) {
      if (st?.type !== 'gallery_image' || !st?._file) { out.push(stripDraft(st)); continue; }
      try {
        const r = ref(storage, `postImages/${me.uid}/sticker_${Date.now()}_${st.id}.jpg`);
        // eslint-disable-next-line no-await-in-loop
        await uploadBytes(r, st._file, { contentType: st._file.type || 'image/jpeg' });
        // eslint-disable-next-line no-await-in-loop
        out.push({ ...stripDraft(st), imageUrl: await getDownloadURL(r) });
      } catch { /* dropped: a sticker nobody but this tab can see is worse than none */ }
    }
    overlay.stickers = out;
  }
  // 🟢 POST AS A PAGE — the app's exact stamping: page identity replaces the byline
  //    AND is carried in dedicated fields so the category badge + PageProfile routing work.
  const identity = asPage ? {
    userName: asPage.name || 'Page',
    userImage: asPage.profileImage || '',
    authorType: 'page',
    pageId: asPage.id,
    pageName: asPage.name || 'Page',
    pageCategory: asPage.category || '',
    pageImage: asPage.profileImage || '',
  } : {
    userName: me.fullName || me.username || 'Allo user',
    userImage: me.profileImage || '',
  };
  const postData = {
    userId: me.uid,
    userEmail: me.email || '',
    ...identity,
    content: text || '',
    sharesCount: 0,
    likes: [],
    comments: [],
    timestamp: serverTimestamp(),
    locationLabel: null,
    status: status || null,
    media,
    audience,
    commentsEnabled: commentsEnabled !== false,
    /* ©️ the author's word that a model made this — written only when true, so
       an ordinary post's document keeps the shape it has always had. The
       server adds what the file itself says (services/contentRights). */
    ...(aiDeclared === true ? { aiDeclared: true } : {}),
    /* 🟢 the look of a text-only post — ids and flags, never colours, so the
       catalogue can be re-tuned without freezing old posts to an old palette.
       null on anything with media; see services/textPost.js. */
    textStyle: textStyle || null,
    ...(first ? {
      /* 🟥 FOR A VIDEO THIS IS THE POSTER, NOT A PICTURE — the same contract the
         phone writes (`imageUrl = videoThumbnailUrl`), which is why every
         renderer can read one field and not care which client posted it. */
      imageUrl: first.type === 'image' ? first.url : posterUrl,
      videoUrl: first.type === 'video' ? first.url : '',
      mediaType: first.type,
    } : {}),
    ...(overlay || {}),
    /* 📊 A POLL. Built by services/vibePoll.js so a person's poll and the one
       the admin portal publishes are the same document — one renderer, one vote
       route, no "works for Allo's polls only" surprises. Spread LAST so nothing
       above can shadow `type`. */
    ...(poll || {}),
  };

  /* 🏷 / ⚠️ HASHTAGS AND THE SAFETY NOTICE — VIDEOS ONLY.
     🟥 THE TEST IS THE UPLOAD, NOT THE PICKER. `media` is what actually reached
        Storage; a video that failed on the way up must not leave a post filed
        under tags no clip will ever be found at.
     🟢 NOBODY TYPED EITHER. The tags are the ones already in the caption and
        the notice is read out of the same sentence, by the same rules the phone
        and the admin server use — services/videoTags. Written only when there
        is something to write, so an ordinary clip's document is the shape it
        has always been. */
  const isVideo = media.some((m) => m.type === 'video');
  const tags = tagsForPost(text, isVideo);
  if (tags.length) postData.hashtags = tags;
  const safety = safetyForPost(text, isVideo);
  if (safety) postData.safety = safety;

  /* 😊 how the author feels / what they are doing — the KEY only, drawn in each
     reader's language (services/postCompose), exactly as the phone writes it */
  const feelingNow = cleanFeeling(feeling);
  if (feelingNow) postData.feeling = feelingNow;
  /* 🎵 "Add music" on a post with no clip — the same fields a video's song uses */
  if (music && !isVideo) {
    const mf = musicFields(music);
    if (mf) Object.assign(postData, mf);
  }

  /* 🏷 WHO THE AUTHOR ASKED TO TAG — only the ask; the server decides who shows
     (tagRoutes.js → tagStatus / tagsApproved) and tells each person */
  const tagList = (Array.isArray(tagged) ? tagged : []).filter((x) => x && x.uid && x.uid !== me.uid).slice(0, 20);
  if (tagList.length) {
    postData.taggedUids = tagList.map((x) => x.uid);
    postData.tagNames = Object.fromEntries(tagList.map((x) => [x.uid, x.name || '']));
  }

  const ref = await addDoc(collection(db, 'posts'), postData);
  if (tagList.length) announceTags(ref.id).catch(() => {});

  /* ©️ THE TWO SERVER PASSES, FIRED THE WAY THE PHONE FIRES THEM. The browser
     never triggered either, so a video posted here waited for the ten-minute
     sweep and an image posted here was never read for provenance at all.
     Fire-and-forget: a lost trigger is caught by the sweeps. */
  for (const m of media) {
    if (m.type === 'video') triggerTranscode(ref.id, m.storagePath);
    else if (m.type === 'image') triggerOptimizeImage(ref.id, m.id, m.storagePath);
  }
  return ref;
}

/* ── friends ──────────────────────────────────────────────────────────────── */

/** Friendship is an accepted friendRequests doc — union of both directions, live. */
export function watchFriends(uid, cb) {
  const state = { in: [], out: [], pendingIn: [], pendingOut: [] };
  const emit = () => {
    const friends = new Map();
    for (const r of [...state.in, ...state.out]) {
      const peer = r.fromUserId === uid
        ? { uid: r.toUserId, name: r.toUserName || '', image: r.toUserImage || '' }
        : { uid: r.fromUserId, name: r.fromUserName || '', image: r.fromUserImage || '' };
      friends.set(peer.uid, { ...peer, reqId: r.id });
    }
    cb({ friends: [...friends.values()], pendingIn: state.pendingIn, pendingOut: state.pendingOut });
  };
  const col = collection(db, 'friendRequests');
  const subs = [
    onSnapshot(query(col, where('toUserId', '==', uid), where('status', '==', 'accepted')),
      (s) => { state.in = s.docs.map((d) => ({ id: d.id, ...d.data() })); emit(); }, () => {}),
    onSnapshot(query(col, where('fromUserId', '==', uid), where('status', '==', 'accepted')),
      (s) => { state.out = s.docs.map((d) => ({ id: d.id, ...d.data() })); emit(); }, () => {}),
    onSnapshot(query(col, where('toUserId', '==', uid), where('status', '==', 'pending')),
      (s) => { state.pendingIn = s.docs.map((d) => ({ id: d.id, ...d.data() })); emit(); }, () => {}),
    onSnapshot(query(col, where('fromUserId', '==', uid), where('status', '==', 'pending')),
      (s) => { state.pendingOut = s.docs.map((d) => ({ id: d.id, ...d.data() })); emit(); }, () => {}),
  ];
  return () => subs.forEach((u) => u());
}

/**
 * 🔒 THE RECIPIENT IS ASKED FIRST, and the answer is theirs to give.
 *
 * 🟥 THIS USED TO BE A WRITE AND NOTHING ELSE. Neither client read the other
 *    account's document before creating a request, so "stop people adding me"
 *    had nowhere to be honoured even once it existed. It is read here, checked
 *    by the same shared function the phone uses, and enforced a third time by
 *    the rule on `friendRequests` — because a check in two apps is a check two
 *    apps can be modified past.
 *
 * @returns true when it was sent, or the reason it was not
 */
export async function sendFriendRequest(me, target) {
  const col = collection(db, 'friendRequests');
  const [a, b, them] = await Promise.all([
    getDocs(query(col, where('fromUserId', '==', me.uid), where('toUserId', '==', target.uid))),
    getDocs(query(col, where('fromUserId', '==', target.uid), where('toUserId', '==', me.uid))),
    getDoc(doc(db, 'users', target.uid)).catch(() => null),
  ]);
  if (!a.empty || !b.empty) return false;
  const block = whyCannotSend(them && them.exists() ? them.data() : null, me.uid, target.uid);
  if (block) return block;
  await addDoc(col, {
    type: 'friend_request',
    fromUserId: me.uid,
    fromUserName: me.fullName || me.username || 'Allo user',
    fromUserImage: me.profileImage || '',
    toUserId: target.uid,
    toUserName: target.name || target.fullName || '',
    status: 'pending',
    timestamp: serverTimestamp(),
    read: false,
  });
  // the app also drops a notifications doc + pushes to the target's phones
  addDoc(collection(db, 'notifications'), {
    type: 'friend_request',
    fromUserId: me.uid,
    fromUserName: me.fullName || me.username || 'Allo user',
    fromUserImage: me.profileImage || '',
    toUserId: target.uid,
    postId: '',
    postContent: 'sent you a friend request',
    timestamp: serverTimestamp(),
    read: false,
  }).catch(() => {});
  pushSocial({
    toUserId: target.uid, type: 'friend_request',
    title: 'New Friend Request', body: `${nameOf(me)} sent you a friend request.`,
    locTitleKey: 'fr_title', locKey: 'fr_body', locParams: { name: nameOf(me) },
    image: me.profileImage, payload: {},
  });
  return true;
}

export async function acceptFriendRequest(req, me) {
  await updateDoc(doc(db, 'friendRequests', req.id), { status: 'accepted', respondedAt: serverTimestamp() });
  notify({ type: 'friend_request_accepted', fromMe: me, toUserId: req.fromUserId, post: null }).catch(() => {});
}

export function declineFriendRequest(reqId) {
  return updateDoc(doc(db, 'friendRequests', reqId), { status: 'declined' });
}

/* ── notifications / my content / pages ───────────────────────────────────── */

export function watchNotifications(uid, cb) {
  const q = query(collection(db, 'notifications'), where('toUserId', '==', uid), orderBy('timestamp', 'desc'));
  return onSnapshot(q, (s) => cb(s.docs.map((d) => ({ id: d.id, ...d.data() }))), () => cb([]));
}

export function markNotificationRead(id) {
  return updateDoc(doc(db, 'notifications', id), { read: true });
}

/** Everything the analytics pane derives comes from my own posts, live. */
export function watchMyPosts(uid, cb) {
  const q = query(collection(db, 'posts'), where('userId', '==', uid));
  return onSnapshot(q, (s) => {
    const posts = s.docs.map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0));
    cb(posts);
  }, () => cb([]));
}

export function watchMyPages(uid, cb) {
  const q = query(collection(db, 'pages'), where('ownerUid', '==', uid));
  return onSnapshot(q, (s) => cb(s.docs.map((d) => ({ id: d.id, ...d.data() }))), () => cb([]));
}

/** A user's profile-page posts — the app's query, page posts filtered out. */
export async function fetchUserPosts(uid) {
  const s = await getDocs(query(collection(db, 'posts'), where('userId', '==', uid)));
  return s.docs.map((d) => ({ id: d.id, ...d.data() }))
    .filter((p) => p.authorType !== 'page')
    .sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0));
}

/** 🏷 The posts a person is tagged in (and allowed) — newest first. A friends-only
 *  post by somebody else shows only to its author and the people in it. */
export async function fetchTaggedPosts(uid, viewerUid) {
  const s = await getDocs(query(collection(db, 'posts'), where('tagsApproved', 'array-contains', String(uid)), limit(100)));
  return s.docs.map((d) => postFromDoc(d.id, d.data()))
    .filter((p) => p.audience !== 'friends' || p.userId === viewerUid || (Array.isArray(p.tagsApproved) && p.tagsApproved.includes(viewerUid)))
    .sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0));
}

export async function fetchPostsByIds(ids) {
  const out = [];
  for (const id of ids.slice(0, 40)) {
    // eslint-disable-next-line no-await-in-loop
    const s = await getDoc(doc(db, 'posts', id)).catch(() => null);
    if (s?.exists()) out.push({ id: s.id, ...s.data() });
  }
  return out;
}

/* ── shares, covers, pages ────────────────────────────────────────────────── */

/** The app's share counters: total, per-user map, and the shares uid list. */
export async function recordShare(post, me) {
  try {
    /* ❤️ the server counts the share (and lists the sharer while there is room);
       not reached → the old write, still capped */
    const counted = await countShare(post.id);
    if (!counted.ok && !counted.status) {
      await updateDoc(doc(db, 'posts', post.id), shareLedgerRoom(post, me.uid)
        ? { sharesCount: increment(1), shares: arrayUnion(me.uid), [`shareCounts.${me.uid}`]: increment(1) }
        : { sharesCount: increment(1) });
    }
  } catch {}
  // share notification to the owner — same feed the app's 'share' type lands in
  try {
    if (post.userId && post.userId !== me.uid) {
      pushSocial({
        toUserId: post.userId, type: 'share',
        title: 'Post Shared', body: `${nameOf(me)} shared your vibe!`,
        locTitleKey: 'share_title', locKey: 'share_body', locParams: { name: nameOf(me) },
        image: me.profileImage, payload: { postId: post.id },
      });
      await addDoc(collection(db, 'notifications'), {
        type: 'share',
        fromUserId: me.uid,
        fromUserName: me.fullName || me.username || 'Someone',
        fromUserImage: me.profileImage || '',
        toUserId: post.userId,
        postId: post.id,
        postContent: (post.content || '').slice(0, 80),
        timestamp: serverTimestamp(),
        read: false,
      });
    }
  } catch {}
}

/**
 * ⭐ SHARE A POST AS A POST — the Facebook "Share to your feed".
 *
 * 🟢 IT IS JUST createPost WITH A DIFFERENT BODY. The document buildSharePost
 *    returns is an ordinary `posts` doc authored by the sharer, so it needs no
 *    new collection, no new rule, and no new query: watchFeed finds it, the
 *    profile grid lists it, likes and comments already work on it. The only new
 *    thing in this function is the counter and who gets told.
 *
 * 🟥 THE COUNTER MOVES ON THE POST THAT WAS ACTUALLY TAPPED, not on the root of
 *    the chain. "Shares" under a card has to mean "times THIS card was shared" or
 *    the number under a popular original would silently absorb every re-share of
 *    every re-share and never match what anyone can see.
 */
export async function sharePostToFeed({ post, me, caption = '', asPage = null }) {
  const data = buildSharePost({ post, me, caption, asPage });
  const created = await addDoc(collection(db, 'posts'), {
    ...data,
    timestamp: serverTimestamp(),
  });

  /* Counters and notifications are best-effort AFTER the share exists: the share
     is the thing the person asked for, and a failed counter must not lose it. */
  countShare(post.id).then((counted) => {
    if (counted.ok || counted.status) return;
    updateDoc(doc(db, 'posts', post.id), shareLedgerRoom(post, me.uid)
      ? { sharesCount: increment(1), shares: arrayUnion(me.uid), [`shareCounts.${me.uid}`]: increment(1) }
      : { sharesCount: increment(1) }).catch(() => {});
  }).catch(() => {});

  for (const uid of shareAudience({ post, sharerUid: me.uid })) {
    pushSocial({
      toUserId: uid, type: 'share',
      title: 'Post Shared', body: `${nameOf(me)} shared your vibe!`,
      locTitleKey: 'share_title', locKey: 'share_body', locParams: { name: nameOf(me) },
      image: me.profileImage, payload: { postId: created.id },
    });
    addDoc(collection(db, 'notifications'), {
      type: 'share',
      fromUserId: me.uid,
      fromUserName: nameOf(me),
      fromUserImage: me.profileImage || '',
      toUserId: uid,
      /* 🟢 THE NOTIFICATION POINTS AT THE SHARE, NOT AT THEIR OWN POST. Tapping
         "Bob shared your vibe" should land on what Bob actually posted — their
         caption and the embed — which is also the only way to reach the thread
         where people are replying to it. */
      postId: created.id,
      postContent: (caption || post.content || '').slice(0, 80),
      timestamp: serverTimestamp(),
      read: false,
    }).catch(() => {});
  }

  return created;
}

/** The app's default cover art (same Unsplash fallback the phone shows). */
export const DEFAULT_COVER = 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=2564&auto=format&fit=crop';

/** Replace my profile cover — storage path + doc field exactly as the app writes
 *  them (coverImages/{uid}/ + users.coverImage), so the phone shows it instantly. */
export async function setCoverImage(uid, file) {
  const r = ref(storage, `coverImages/${uid}/${Date.now()}.jpg`);
  await uploadBytes(r, file, { contentType: file.type || 'image/jpeg' });
  const url = await getDownloadURL(r);
  await updateDoc(doc(db, 'users', uid), { coverImage: url });
  return url;
}

/* ══ A PAGE'S OWN PICTURES ═════════════════════════════════════════════════
   🟥 BOTH LIVE UNDER `pageImages/{pageId}/`, which is where the app's PageWizard
      already writes a new page's photo. That path is therefore known to pass
      Storage rules; anything invented next to it (coverImages/…, pages/…) is a
      guess that fails at upload time on someone else's device.
   🟥 AND contentType IS SET. An upload with no metadata lands as
      application/octet-stream — the same omission that made post images
      download instead of display. */

export async function setPageCover(pageId, file) {
  const r = ref(storage, `pageImages/${pageId}/cover_${Date.now()}.jpg`);
  await uploadBytes(r, file, { contentType: file.type || 'image/jpeg' });
  const url = await getDownloadURL(r);
  await updateDoc(doc(db, 'pages', pageId), { coverImage: url });
  return url;
}

/**
 * Replace a page's photo.
 *
 * 🟥 AND RE-STAMP ITS POSTS. A page post carries a COPY of the page's name and
 *    picture (`pageImage`/`userImage`), stamped at post time so a feed row needs
 *    no second read to render. Change the page and say nothing, and every post
 *    it has ever made keeps the old photo forever — a bug the app already hit
 *    and fixed in PageWizard. Batched in 400s (the 500 limit), and fail-soft:
 *    the photo change itself must not fail because a backfill did.
 */
export async function setPageImage(pageId, file) {
  const r = ref(storage, `pageImages/${pageId}/profile_${Date.now()}.jpg`);
  await uploadBytes(r, file, { contentType: file.type || 'image/jpeg' });
  const url = await getDownloadURL(r);
  await updateDoc(doc(db, 'pages', pageId), { profileImage: url });
  try {
    const snap = await getDocs(query(collection(db, 'posts'), where('pageId', '==', pageId)));
    let batch = writeBatch(db);
    let n = 0;
    const commits = [];
    snap.forEach((d) => {
      batch.update(d.ref, { pageImage: url, userImage: url });
      n += 1;
      if (n % 400 === 0) { commits.push(batch.commit()); batch = writeBatch(db); }
    });
    if (n % 400 !== 0) commits.push(batch.commit());
    await Promise.all(commits);
  } catch { /* old posts keep the old stamp rather than the change failing */ }
  return url;
}

export async function fetchProfileMeta(uid) {
  try {
    const s = await getDoc(doc(db, 'users', uid));
    const d = s.exists() ? s.data() : {};
    return {
      coverImage: d.coverImage || null,
      fullName: d.fullName || '',
      username: d.username || '',
      profileImage: d.profileImage || '',
      bio: d.bio || '',
      /* 🔒 THE PHONE'S FIELD, READ AT LAST. `users/{uid}.isPrivate` has been
         written by the app for as long as the setting has existed and nothing
         here ever read it, so the same account looked unlocked in the browser
         and locked on the phone. Nothing needed migrating; what was missing was
         a second reader. See services/profilePrivacy. */
      isPrivate: !!d.isPrivate,
      /* 🪪 THE OPTIONAL ANSWERS, AND ONLY THE PUBLIC ONES. There is no filter
         here and there does not need to be: an answer its owner kept back is
         not on this document at all, it is in a subcollection only they may
         read. See app/services/profileStore.ts. */
      profileFields: d.profileFields || {},
      /* 🔒 so the profile pane can say why the button is not there */
      privacySettings: { friendRequests: d?.privacySettings?.friendRequests || 'everyone' },
    };
  } catch { return {}; }
}

/**
 * 🔒 Public or private — the phone's boolean, written the phone's way.
 *
 * 🟥 THE SAME FIELD, NOT A SECOND ONE. A `webPrivacy` alongside `isPrivate`
 *    would have meant two settings for one idea and a support question nobody
 *    can answer. Both clients already listen to this document, so a change here
 *    is live on the phone with no sync step at all.
 */
export function setProfilePrivacy(uid, isPrivate) {
  return updateDoc(doc(db, 'users', uid), { isPrivate: !!isPrivate });
}

export async function fetchPage(pageId) {
  try {
    const s = await getDoc(doc(db, 'pages', pageId));
    return s.exists() ? { id: s.id, ...s.data() } : null;
  } catch { return null; }
}

export async function fetchPagePosts(pageId) {
  try {
    const snap = await getDocs(query(collection(db, 'posts'), where('pageId', '==', pageId)));
    const out = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    out.sort((a, b) => (b.timestamp?.toMillis?.() || 0) - (a.timestamp?.toMillis?.() || 0));
    return out;
  } catch { return []; }
}

export async function followPage(pageId, uid, isFollowing) {
  return updateDoc(doc(db, 'pages', pageId), isFollowing
    ? { followers: arrayRemove(uid), followersCount: increment(-1) }
    : { followers: arrayUnion(uid), followersCount: increment(1) });
}

/** The app's page categories (PageWizard) — same ids, so badges match everywhere. */
export const PAGE_CATEGORIES = [
  'business', 'shop', 'restaurant', 'sport', 'creator', 'education',
  'health', 'technology', 'news', 'entertainment', 'community',
  /* 🟢 a real person running a page under their own name — the category that
     makes pronouns and a hometown sensible questions to ask */
  'public_figure', 'other',
];

/** Create a page with the app's exact schema (PageWizard parity). */
export async function createPage({
  me, name, category, bio = '', photoFile = null,
  website = '', location = '', hours = '', phone = '',
  /* 🪪 the optional identity answers, and the ones kept back. `fields` goes on
     the page; `held` goes to a subdocument only the owner may read — see
     app/services/profileStore.ts for why those are different places. */
  fields = {}, held = {},
}) {
  const pageRef = doc(collection(db, 'pages'));
  let profileImage = '';
  if (photoFile) {
    const r = ref(storage, `pageImages/${pageRef.id}/profile_${Date.now()}.jpg`);
    await uploadBytes(r, photoFile, { contentType: photoFile.type || 'image/jpeg' });
    profileImage = await getDownloadURL(r);
  }
  await setDoc(pageRef, {
    id: pageRef.id,
    ownerUid: me.uid,
    name: name.trim(),
    nameLower: name.trim().toLowerCase(),
    category,
    bio: bio.trim(),
    profileImage,
    website: website.trim(), location: location.trim(), hours: hours.trim(), phone: phone.trim(),
    extra: {},
    profileFields: fields,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    followers: [],
    followersCount: 0,
  });
  /* 🟥 WRITTEN EVEN WHEN EMPTY, so the owner-only document exists from the first
     save and hiding something later is an update rather than a create nobody
     tested. Its own write rather than part of the batch above only because the
     page has to exist before anything can live under it. */
  await setDoc(doc(db, ...hiddenPath('page', pageRef.id)), {
    fields: held, updatedAt: serverTimestamp(),
  }).catch(() => {});
  return pageRef.id;
}
